import { SelectFilterConfig, TooltipV2, ButtonV2, FilterV2, SearchV2, SortV2 } from '@cimplico/components';
import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toastNotification } from '../../components/toast/Toast';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useChartOfAccounts } from '../../queries/use-chart-of-accounts';
import { useRefreshJournals } from '../../queries/use-refresh-journals';
import { saveFilterV2, saveSortV2 } from '../../slices/workpaperFilterSlice';
import { RootState } from '../../store';
import { WorkpaperSourceEnum } from '../../types/workpaper';
import { formatDateTime, calcRelativeTime } from '../../utilities/formatting';
import plural from '../../utilities/plural';
import { string } from 'zod';
import { JournalStatusEnum } from '../../types/journal';

type JournalsPagePrimaryActionsProps = {
    defaultSearchValue?: string;
    showStatusConfig?: boolean;
    showAccountsConfig?: boolean;
    searchRef?: React.MutableRefObject<HTMLInputElement | undefined>;
    disablePrimaryButton?: boolean;
    onXeroError: (error?: { name: string; message: string }) => void;
    onSearch: (search: string) => void;
    onSort: (sort: string) => void;
    onFilter: (filter: string) => void;
    onNewJournalClick?: () => void;
};

const sortOptions = [
    { label: 'Reference', value: 'reference' },
    { label: 'Narration', value: 'narration' },
    { label: 'Debit', value: 'debitTotal' },
    { label: 'Credit', value: 'creditTotal' },
    { label: 'Lines', value: 'linesCount' },
    { label: 'Accounts', value: 'accountsCount' },
    { label: 'Date', value: 'date' },
    { label: 'Last Updated', value: 'updatedAt' },
];

function JournalsPagePrimaryActions({
    defaultSearchValue = '',
    showStatusConfig = true,
    showAccountsConfig = true,
    searchRef,
    disablePrimaryButton = false,
    onXeroError,
    onFilter,
    onSearch,
    onSort,
    onNewJournalClick,
}: JournalsPagePrimaryActionsProps): JSX.Element {
    const { id: workpaperId, provider, journalRefreshedAt } = useWorkpaperContext();
    const refreshJournalQuery = useRefreshJournals();
    const accountsQuery = useChartOfAccounts({
        page: 1,
        searchQuery: '',
        sortQuery: 'sort[dir]=asc&sort[field]=code',
        filterQuery: '',
    });

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaperId]?.journals);
    const dispatch = useDispatch();

    function handleSortChange({ queryString }: { queryString: string }): void {
        dispatch(saveSortV2({ workpaperId, section: 'journals', query: queryString }));
        onSort(queryString);
    }

    function handleFilterChange({ queryString }: { queryString: string }): void {
        dispatch(saveFilterV2({ workpaperId, section: 'journals', query: queryString }));
        onFilter(queryString);
    }

    function handleSearchChange(result: string): void {
        onSearch(result);
    }

    function handleRefresh(): void {
        refreshJournalQuery.mutate('', {
            onSuccess: (res) => {
                toastNotification(
                    'success',
                    'Journals refreshed!',
                    `${res.updated} ${plural(res.updated, 'journal', 'journals')} updated.`
                );
            },
            onError: (res) => {
                const error = res.response?.data.error;
                const message = res.response?.data.message;

                if (error && message) {
                    onXeroError({ message, name: error });
                }
                toastNotification('error', 'Unable to refresh journals!', message);
            },
        });
    }

    const accounts = savedFilters?.filter?.find((f) => f.id === 'accounts' && f.type === 'select') as SelectFilterConfig | undefined;

    const accountOptions = useMemo(() => {
        return (
            accountsQuery?.data?.accounts.map((a) => ({
                label: `${a.code} - ${a.description}`,
                value: a.id,
                active: accounts?.options.find((o) => o.value === a.id && o.active) !== undefined,
            })) || []
        );
    }, [accounts?.options, accountsQuery?.data?.accounts]);

    return (
        <div className="flex gap-2 items-center my-4 w-full">
            <FilterV2 onChange={handleFilterChange}>
                {showStatusConfig && (
                    <FilterV2.CheckboxFilter
                        options={[
                            { label: 'Draft', value: JournalStatusEnum.DRAFT },
                            { label: 'Posted', value: JournalStatusEnum.POSTED },
                            { label: 'Voided', value: JournalStatusEnum.VOIDED },
                            { label: 'Archived', value: JournalStatusEnum.ARCHIVED },
                        ]}
                        id="status"
                        label="Status"
                    />
                )}
                <FilterV2.DateFilter id="date" label="Date" />
                <FilterV2.NumericFilter id="debitTotal" label="Debit" />
                <FilterV2.NumericFilter id="creditTotal" label="Credit" />
                <FilterV2.NumericFilter id="linesCount" label="Lines" />
                {showAccountsConfig && <FilterV2.SelectFilter id="accounts" label="Account" values={[]} options={accountOptions} />}
            </FilterV2>

            <SearchV2
                onChange={handleSearchChange}
                placeholder="Search journals..."
                inputRef={searchRef}
                defaultValue={defaultSearchValue}
            />

            <SortV2
                onChange={handleSortChange}
                options={showStatusConfig ? [{ label: 'Status', value: 'status' }, ...sortOptions] : sortOptions}
                defaultDirection="asc"
                defaultValue="reference"
            />
            <div className="ml-auto flex items-center gap-2">
                {onNewJournalClick && (
                    <ButtonV2
                        type="secondary"
                        label="New Journal"
                        size="sm"
                        onClick={onNewJournalClick}
                        isDisabled={disablePrimaryButton}
                    />
                )}
                {provider === WorkpaperSourceEnum.XERO && (
                    <>
                        <ButtonV2
                            onClick={handleRefresh}
                            type="primary"
                            label="Update"
                            size="sm"
                            isLoading={refreshJournalQuery.isLoading}
                            isDisabled={refreshJournalQuery.isLoading || disablePrimaryButton}
                        />
                        {journalRefreshedAt && (
                            <div>
                                <TooltipV2 message={formatDateTime(journalRefreshedAt)}>
                                    <p className="text-gray-700 text-xs">{calcRelativeTime(journalRefreshedAt)}</p>
                                </TooltipV2>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
export default JournalsPagePrimaryActions;
